export type GtagFunction = (...args: unknown[]) => void;

type GtagWindow = {
  dataLayer?: unknown[][];
  gtag?: GtagFunction;
  __oneTranscribeGtagLoaded?: boolean;
};

export type GtagClientDependencies = {
  gtag?: GtagFunction;
  load?: () => void;
  getWindow?: () => GtagWindow | undefined;
  appendScript?: (src: string) => void;
};

const _browserWindow = (): GtagWindow | undefined =>
  typeof window === "undefined"
    ? undefined
    : (window as unknown as GtagWindow);

const _appendBrowserScript = (src: string): void => {
  if (typeof document === "undefined") return;
  const script = document.createElement("script");
  script.async = true;
  script.src = src;
  document.head.appendChild(script);
};

const _createBrowserLoader = (
  trackingId: string,
  getWindow: () => GtagWindow | undefined,
  appendScript: (src: string) => void,
) => (): void => {
  const target = getWindow();
  if (!target) return;
  if (!target.gtag) {
    target.dataLayer ||= [];
    target.gtag = (...args: unknown[]) => target.dataLayer?.push(args);
  }
  if (target.__oneTranscribeGtagLoaded) return;
  target.__oneTranscribeGtagLoaded = true;
  target.gtag("js", new Date());
  target.gtag("config", trackingId);
  appendScript(
    `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(trackingId)}`,
  );
};

export const createGtagClient = (
  trackingId: string,
  dependencies: GtagClientDependencies = {},
) => {
  const getWindow = dependencies.getWindow || _browserWindow;
  const load =
    dependencies.load ||
    _createBrowserLoader(
      trackingId,
      getWindow,
      dependencies.appendScript || _appendBrowserScript,
    );
  const call = (...args: unknown[]): void => {
    load();
    (dependencies.gtag || getWindow()?.gtag)?.(...args);
  };

  return {
    pageview: (path: string) => {
      call("config", trackingId, {
        page_path: path,
      });
    },
    event: (name: string, data: Record<string, unknown> = {}) => {
      call("event", name, data);
    },
    userData: (email: string) => {
      const normalized = email.trim().toLowerCase();
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(normalized)) return;
      call("set", "user_data", {
        email: normalized,
      });
    },
    conversion: (input: {
      value: number;
      transactionId: string;
      currency: string;
    }) => {
      call("event", "conversion", {
        send_to: "AW-318426315/GtFaCMau1u0ZEMuZ65cB",
        value: input.value,
        currency: input.currency,
        transaction_id: input.transactionId,
      });
    },
  };
};

export type GtagClient = ReturnType<typeof createGtagClient>;
